import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { useUpdateReport } from "../data";
import styles from "./PolicyFieldsCard.module.css";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { TabView, TabPanel } from "primereact/tabview";
import * as client from "../../../client";
import { readableNameFromPeril } from "../general-input/util";
import { cloneDeep, isEqual } from "lodash";
import { formatMonetaryValue } from "./util";
import { getPolicySourceNames } from "../utils";

const PERIL_OPTIONS: { label: string; value: client.peril_type }[] = (
  Object.values(client.peril_type) as client.peril_type[]
).map((p) => ({ label: readableNameFromPeril(p), value: p }));

/** Small dialog component for adding a manual limit or deductible with PrimeReact controls. */
const AddManualEntryDialog: React.FC<{
  visible: boolean;
  header: string;
  namePlaceholder: string;
  descriptionPlaceholder: string;
  onHide: () => void;
  onConfirm: (
    peril: client.peril_type,
    name: string,
    description: string
  ) => void;
}> = ({
  visible,
  header,
  namePlaceholder,
  descriptionPlaceholder,
  onHide,
  onConfirm,
}) => {
  const [peril, setPeril] = useState<client.peril_type | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const reset = () => {
    setPeril(null);
    setName("");
    setDescription("");
  };

  return (
    <Dialog
      visible={visible}
      header={header}
      style={{ width: "400px" }}
      onHide={() => {
        reset();
        onHide();
      }}
      footer={
        <div>
          <Button
            label="Cancel"
            text
            severity="secondary"
            onClick={() => {
              reset();
              onHide();
            }}
          />
          <Button
            label="Confirm"
            disabled={!peril}
            onClick={() => {
              if (peril) {
                onConfirm(peril, name, description);
                reset();
                onHide();
              }
            }}
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        <Dropdown
          options={PERIL_OPTIONS}
          optionLabel="label"
          optionValue="value"
          placeholder="Select peril type"
          className={styles.confirmDialogWidth}
          value={peril}
          onChange={(e) => setPeril(e.value)}
        />
        <InputText
          className={styles.confirmDialogWidth}
          placeholder={namePlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <InputText
          className={styles.confirmDialogWidth}
          placeholder={descriptionPlaceholder}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </Dialog>
  );
};

interface PolicyFieldsCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

type DeductibleRow = {
  perilType: client.peril_type;
  readablePerilName: string;
  deductibleName: string;
  value: string;
  manual: boolean;
};

type LimitRow = {
  perilType: client.peril_type;
  readablePerilName: string;
  limitName: string;
  value: string;
  manual: boolean;
};

const classificationOrder: Record<string, number> = {
  "": 0,
  time_limit: 1,
  distance_limit: 2,
  waiting_period: 3,
};

const readableClassification = (c: string | null | undefined): string => {
  switch (c) {
    case "time_limit":
      return "Time Limits";
    case "distance_limit":
      return "Distance Limits";
    case "waiting_period":
      return "Waiting Periods";
    default:
      return "Default";
  }
};

/** Build flattened deductible rows from an AdvancedDeductibleInfo for a given policy.
 *  Manual entries take precedence: if a peril exists in manualDeductibles, extracted entries for that peril are skipped. */
const buildDeductibleRows = (
  extractedForPolicy: Record<string, client.AdvancedDeductible>,
  manualDeductibles: Record<string, client.AdvancedDeductible>
): DeductibleRow[] => {
  const rows: DeductibleRow[] = [];
  const manualPerilKeys = new Set(Object.keys(manualDeductibles));
  for (const [perilKey, ded] of Object.entries(extractedForPolicy)) {
    if (manualPerilKeys.has(perilKey)) continue;
    const excerpts = {
      ...ded.direct_excerpts,
      ...ded.manual_excerpt_overrides,
    };
    for (const [name, value] of Object.entries(excerpts)) {
      rows.push({
        perilType: perilKey as client.peril_type,
        readablePerilName: readableNameFromPeril(perilKey as client.peril_type),
        deductibleName: name,
        value,
        manual: ded.manual_excerpt_overrides?.[name] != null,
      });
    }
  }
  for (const [perilKey, ded] of Object.entries(manualDeductibles)) {
    const excerpts = {
      ...ded.direct_excerpts,
      ...ded.manual_excerpt_overrides,
    };
    for (const [name, value] of Object.entries(excerpts)) {
      rows.push({
        perilType: perilKey as client.peril_type,
        readablePerilName: readableNameFromPeril(perilKey as client.peril_type),
        deductibleName: name,
        value,
        manual: true,
      });
    }
  }
  return rows;
};

/** Build flattened limit rows from an AdvancedLimitInfo for a given policy.
 *  Manual entries take precedence: if a peril exists in manualLimits, extracted entries for that peril are skipped. */
const buildLimitRows = (
  extractedForPolicy: Record<string, client.AdvancedLimit>,
  manualLimits: Record<string, client.AdvancedLimit>
): LimitRow[] => {
  const rows: LimitRow[] = [];
  const manualPerilKeys = new Set(Object.keys(manualLimits));
  for (const [perilKey, lim] of Object.entries(extractedForPolicy)) {
    if (manualPerilKeys.has(perilKey)) continue;
    const excerpts = {
      ...lim.direct_excerpts,
      ...lim.manual_excerpt_overrides,
    };
    for (const [name, value] of Object.entries(excerpts)) {
      rows.push({
        perilType: perilKey as client.peril_type,
        readablePerilName: readableNameFromPeril(perilKey as client.peril_type),
        limitName: name,
        value,
        manual: lim.manual_excerpt_overrides?.[name] != null,
      });
    }
  }
  for (const [perilKey, lim] of Object.entries(manualLimits)) {
    const excerpts = {
      ...lim.direct_excerpts,
      ...lim.manual_excerpt_overrides,
    };
    for (const [name, value] of Object.entries(excerpts)) {
      rows.push({
        perilType: perilKey as client.peril_type,
        readablePerilName: readableNameFromPeril(perilKey as client.peril_type),
        limitName: name,
        value,
        manual: true,
      });
    }
  }
  return rows;
};

export const PolicyFieldsCard: React.FC<PolicyFieldsCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  // --- Sublimit source data (not cloned, sublimits save immediately) ---
  const manualSublimits =
    specialtyPropertyInfo.report_json.sublimit_info?.manual_sublimits ?? [];
  const extractedSublimitInfo =
    specialtyPropertyInfo.report_json.sublimit_info?.extracted_sublimits ?? {};

  // --- Cloned state for deductibles and limits ---
  const serverDeductibleInfo =
    specialtyPropertyInfo.report_json.deductible_info;
  const serverLimitInfo = specialtyPropertyInfo.report_json.limit_info;

  const [deductibleInfo, setDeductibleInfo] = useState(
    () => serverDeductibleInfo && cloneDeep(serverDeductibleInfo)
  );
  const [limitInfo, setLimitInfo] = useState(
    () => serverLimitInfo && cloneDeep(serverLimitInfo)
  );

  useEffect(() => {
    setDeductibleInfo(serverDeductibleInfo && cloneDeep(serverDeductibleInfo));
  }, [serverDeductibleInfo]);

  useEffect(() => {
    setLimitInfo(serverLimitInfo && cloneDeep(serverLimitInfo));
  }, [serverLimitInfo]);

  // --- Dirty detection ---
  const deductiblesDirty = !isEqual(deductibleInfo, serverDeductibleInfo);
  const limitsDirty = !isEqual(limitInfo, serverLimitInfo);
  const hasPendingEdits = deductiblesDirty || limitsDirty;

  // --- Policy selector ---
  const policyNames = getPolicySourceNames(
    specialtyPropertyInfo.report_json.sublimit_info,
    serverDeductibleInfo,
    serverLimitInfo
  );
  const allPolicyValues = policyNames.map((name) => ({
    code: name,
    label: name,
  }));
  const [selectedExtractedPolicy, setSelectedExtractedPolicy] = useState(
    specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name &&
      policyNames.includes(
        specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name
      )
      ? {
          code: specialtyPropertyInfo.report_json.policy_info
            ?.selected_policy_form_name,
          label:
            specialtyPropertyInfo.report_json.policy_info
              ?.selected_policy_form_name,
        }
      : undefined
  );

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [addLimitVisible, setAddLimitVisible] = useState(false);
  const [addDeductibleVisible, setAddDeductibleVisible] = useState(false);

  // --- Sublimit rows ---
  const extractedSublimits = selectedExtractedPolicy?.code
    ? (extractedSublimitInfo[selectedExtractedPolicy.code] ?? [])
    : [];

  const allSublimits = manualSublimits
    .map((limit) => ({ ...limit, manual: true }))
    .concat(
      extractedSublimits
        .filter((limit) => !limit.is_removed)
        .map((limit) => ({ ...limit, manual: false }))
    )
    .map((limit) => ({
      ...limit,
      classificationGroup: limit.classification ?? "",
    }))
    .sort(
      (a, b) =>
        (classificationOrder[a.classificationGroup] ?? 99) -
        (classificationOrder[b.classificationGroup] ?? 99)
    );

  // --- Deductible rows (derived from local state) ---
  const extractedDeductibles = deductibleInfo?.extracted_deductibles ?? {};
  const manualDeductibles = deductibleInfo?.manual_deductibles ?? {};
  const extractedDeductiblesForPolicy = selectedExtractedPolicy?.code
    ? (extractedDeductibles[selectedExtractedPolicy.code] ?? {})
    : {};
  const deductibleRows = buildDeductibleRows(
    extractedDeductiblesForPolicy,
    manualDeductibles
  );

  // --- Limit rows (derived from local state) ---
  const extractedLimitsAdvanced = limitInfo?.extracted_limits ?? {};
  const manualLimitsAdvanced = limitInfo?.manual_limits ?? {};
  const extractedLimitsForPolicy = selectedExtractedPolicy?.code
    ? (extractedLimitsAdvanced[selectedExtractedPolicy.code] ?? {})
    : {};
  const limitRows = buildLimitRows(
    extractedLimitsForPolicy,
    manualLimitsAdvanced
  );

  // --- Deductible edit handler: mutates local state ---
  const handleDeductibleEdit = (data: DeductibleRow) => {
    let newValue = data.value;
    confirmDialog({
      message: (
        <div className={styles.verticalContainer}>
          <InputText
            className={styles.confirmDialogWidth}
            defaultValue={newValue}
            onChange={(e) => {
              newValue = e.target.value;
            }}
          />
        </div>
      ),
      pt: { message: { style: { width: "300px" } } },
      style: { width: "400px" },
      header: `Edit ${data.deductibleName}`,
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      accept: () => {
        newValue = formatMonetaryValue(newValue);
        setDeductibleInfo((prev) => {
          const next = cloneDeep(
            prev ?? { extracted_deductibles: {}, manual_deductibles: {} }
          );
          const manualExisting = next.manual_deductibles[data.perilType];
          if (manualExisting) {
            manualExisting.manual_excerpt_overrides = {
              ...manualExisting.manual_excerpt_overrides,
              [data.deductibleName]: newValue,
            };
          } else if (selectedExtractedPolicy?.code) {
            const existing =
              next.extracted_deductibles[selectedExtractedPolicy.code]?.[
                data.perilType
              ];
            if (existing) {
              existing.manual_excerpt_overrides = {
                ...existing.manual_excerpt_overrides,
                [data.deductibleName]: newValue,
              };
            }
          }
          return next;
        });
      },
    });
  };

  // --- Limit edit handler: mutates local state ---
  const handleLimitEdit = (data: LimitRow) => {
    let newValue = data.value;
    confirmDialog({
      message: (
        <div className={styles.verticalContainer}>
          <InputText
            className={styles.confirmDialogWidth}
            defaultValue={newValue}
            onChange={(e) => {
              newValue = e.target.value;
            }}
          />
        </div>
      ),
      pt: { message: { style: { width: "300px" } } },
      style: { width: "400px" },
      header: `Edit ${data.limitName}`,
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      accept: () => {
        newValue = formatMonetaryValue(newValue);
        setLimitInfo((prev) => {
          const next = cloneDeep(
            prev ?? { extracted_limits: {}, manual_limits: {} }
          );
          const manualExisting = next.manual_limits[data.perilType];
          if (manualExisting) {
            manualExisting.manual_excerpt_overrides = {
              ...manualExisting.manual_excerpt_overrides,
              [data.limitName]: newValue,
            };
          } else if (selectedExtractedPolicy?.code) {
            const existing =
              next.extracted_limits[selectedExtractedPolicy.code]?.[
                data.perilType
              ];
            if (existing) {
              existing.manual_excerpt_overrides = {
                ...existing.manual_excerpt_overrides,
                [data.limitName]: newValue,
              };
            }
          }
          return next;
        });
      },
    });
  };

  // --- Save: send current state to server ---
  const handleSave = () => {
    const update: Parameters<typeof updateReport.mutate>[0] = {};
    if (deductiblesDirty && deductibleInfo) {
      update.deductibleInfo = deductibleInfo;
      update.rerunReport = true;
    }
    if (limitsDirty && limitInfo) {
      update.advancedLimitInfo = limitInfo;
      update.rerunReport = true;
    }
    updateReport.mutate(update);
  };

  // --- Cancel: reset state back to server data ---
  const handleCancel = () => {
    setDeductibleInfo(serverDeductibleInfo && cloneDeep(serverDeductibleInfo));
    setLimitInfo(serverLimitInfo && cloneDeep(serverLimitInfo));
  };

  const saveLabel =
    activeTabIndex === 0 ? "Save edits" : "Save edits and rerun";

  return (
    <Card title="Policy Fields">
      <div
        className={styles.horizontalContainer}
        style={{ marginBottom: "16px" }}
      >
        <Dropdown
          value={selectedExtractedPolicy}
          options={allPolicyValues}
          onChange={(e) => setSelectedExtractedPolicy(e.value)}
          optionLabel="label"
          style={{ maxWidth: "440px" }}
        />
        {hasPendingEdits && (
          <div className={styles.horizontalContainer}>
            <Button
              size="small"
              label={saveLabel}
              icon="pi pi-save"
              disabled={updateReport.isPending}
              onClick={handleSave}
            />
            <Button
              size="small"
              label="Cancel"
              severity="secondary"
              text
              disabled={updateReport.isPending}
              onClick={handleCancel}
            />
          </div>
        )}
      </div>
      <TabView
        activeIndex={activeTabIndex}
        onTabChange={(e) => setActiveTabIndex(e.index)}
      >
        <TabPanel header="Limits">
          <div style={{ marginBottom: "12px" }}>
            <Button
              size="small"
              disabled={updateReport.isPending}
              icon="pi pi-plus"
              label="Add limit"
              onClick={() => setAddLimitVisible(true)}
            />
            <AddManualEntryDialog
              visible={addLimitVisible}
              header="Add Limit"
              namePlaceholder="Limit name"
              descriptionPlaceholder="Limit description"
              onHide={() => setAddLimitVisible(false)}
              onConfirm={(peril, limitName, limitValue) => {
                const formatted = formatMonetaryValue(limitValue);
                setLimitInfo((prev) => {
                  const next = cloneDeep(
                    prev ?? { extracted_limits: {}, manual_limits: {} }
                  );
                  const existing = next.manual_limits[peril];
                  if (existing) {
                    existing.manual_excerpt_overrides = {
                      ...existing.manual_excerpt_overrides,
                      [limitName]: formatted,
                    };
                  } else {
                    next.manual_limits[peril] = {
                      peril_type: peril,
                      short_limit_description: "",
                      document_quotation: "",
                      executable_python_function_str: "",
                      direct_excerpts: {},
                      manual_excerpt_overrides: {
                        [limitName]: formatted,
                      },
                    };
                  }
                  return next;
                });
              }}
            />
          </div>
          <DataTable
            value={limitRows}
            scrollable
            scrollHeight={"70vh"}
            size="small"
            rowGroupMode="subheader"
            groupRowsBy="readablePerilName"
            rowGroupHeaderTemplate={(data: LimitRow) => (
              <strong>{data.readablePerilName}</strong>
            )}
            sortField="readablePerilName"
            sortOrder={1}
          >
            <Column header="Limit" field="limitName" />
            <Column header="Value" field="value" />
            <Column
              header="From"
              body={(data: LimitRow) => (
                <>
                  {data.manual ? (
                    <i className="pi pi-user" />
                  ) : (
                    <i className="pi pi-file" />
                  )}
                </>
              )}
            />
            <Column
              header=""
              body={(data: LimitRow) => (
                <Button
                  icon="pi pi-pencil"
                  style={{ width: "28px", height: "28px" }}
                  text
                  disabled={updateReport.isPending}
                  onClick={() => handleLimitEdit(data)}
                />
              )}
            />
          </DataTable>
        </TabPanel>
        <TabPanel header="Sublimits">
          <div style={{ marginBottom: "12px" }}>
            <Button
              size="small"
              disabled={updateReport.isPending}
              icon="pi pi-plus"
              label="Add sublimit"
              onClick={() => {
                let sublimitName = "";
                let sublimitValue = "";
                confirmDialog({
                  message: (
                    <div className={styles.verticalContainer}>
                      <div className={styles.horizontalContainer}>
                        <InputText
                          className={styles.confirmDialogWidth}
                          placeholder="Sublimit name"
                          onChange={(e) => {
                            sublimitName = e.target.value;
                          }}
                        />
                      </div>
                      <div className={styles.horizontalContainer}>
                        <InputText
                          className={styles.confirmDialogWidth}
                          placeholder="Sublimit value"
                          onChange={(e) => {
                            sublimitValue = e.target.value;
                          }}
                        />
                      </div>
                    </div>
                  ),
                  pt: { message: { style: { width: "300px" } } },
                  style: { width: "400px" },
                  header: "Add Sublimit",
                  acceptLabel: "Confirm",
                  rejectLabel: "Cancel",
                  accept: async () => {
                    const manualSublimitsWithNewValue = [
                      ...manualSublimits,
                      {
                        sublimit_name: sublimitName,
                        sublimit_description:
                          formatMonetaryValue(sublimitValue),
                        document_excerpt: null,
                      },
                    ];
                    updateReport.mutate({
                      sublimitInfo: {
                        extracted_sublimits: extractedSublimitInfo,
                        manual_sublimits: manualSublimitsWithNewValue,
                      },
                    });
                  },
                });
              }}
            />
          </div>
          <DataTable
            value={allSublimits}
            scrollable
            scrollHeight={"70vh"}
            size="small"
            dataKey="sublimit_name"
            rowGroupMode="subheader"
            groupRowsBy="classificationGroup"
            rowGroupHeaderTemplate={(data) => {
              const label = readableClassification(data.classification);
              return label ? <strong>{label}</strong> : null;
            }}
          >
            <Column header="Sublimit" field="sublimit_name" />
            <Column
              header="Value"
              body={(data) => data.manual_override ?? data.sublimit_description}
            />
            <Column
              header="Excerpt"
              body={(data) =>
                data.document_excerpt && (
                  <TooltipWrapper
                    tooltipText={data.document_excerpt}
                    tooltipProps={{ position: "left" }}
                  >
                    <i className="pi pi-info-circle" />
                  </TooltipWrapper>
                )
              }
            />
            <Column
              header="From"
              body={(data: client.Sublimit & { manual: boolean }) => (
                <>
                  {data.manual ||
                  (data.manual_override &&
                    data.manual_override !== data.sublimit_description) ? (
                    <i className="pi pi-user" />
                  ) : (
                    <i className="pi pi-file" />
                  )}
                </>
              )}
            />
            <Column
              header=""
              body={(data) => {
                const handleEdit = () => {
                  let newValue =
                    data.manual_override ?? data.sublimit_description;
                  confirmDialog({
                    message: (
                      <div className={styles.verticalContainer}>
                        <InputText
                          className={styles.confirmDialogWidth}
                          defaultValue={newValue}
                          onChange={(e) => {
                            newValue = e.target.value;
                          }}
                        />
                      </div>
                    ),
                    pt: { message: { style: { width: "300px" } } },
                    style: { width: "400px" },
                    header: "Edit Sublimit Value",
                    acceptLabel: "Confirm",
                    rejectLabel: "Cancel",
                    accept: async () => {
                      newValue = formatMonetaryValue(newValue);
                      if (data.manual) {
                        updateReport.mutate({
                          sublimitInfo: {
                            extracted_sublimits: extractedSublimitInfo,
                            manual_sublimits: manualSublimits.map((limit) =>
                              limit.sublimit_name === data.sublimit_name
                                ? {
                                    ...limit,
                                    sublimit_description: newValue,
                                  }
                                : limit
                            ),
                          },
                        });
                      } else if (selectedExtractedPolicy?.code) {
                        const policyKey = selectedExtractedPolicy.code;
                        updateReport.mutate({
                          sublimitInfo: {
                            extracted_sublimits: {
                              ...extractedSublimitInfo,
                              [policyKey]: extractedSublimitInfo[policyKey].map(
                                (limit) =>
                                  limit.sublimit_name === data.sublimit_name
                                    ? {
                                        ...limit,
                                        manual_override: newValue,
                                      }
                                    : limit
                              ),
                            },
                            manual_sublimits: manualSublimits,
                          },
                        });
                      }
                    },
                  });
                };

                const handleDelete = () => {
                  if (data.manual) {
                    updateReport.mutate({
                      sublimitInfo: {
                        extracted_sublimits: extractedSublimitInfo,
                        manual_sublimits: manualSublimits.filter(
                          (limit) =>
                            limit.sublimit_name !== data.sublimit_name ||
                            limit.sublimit_description !==
                              data.sublimit_description
                        ),
                      },
                    });
                  } else if (selectedExtractedPolicy?.code) {
                    const policyKey = selectedExtractedPolicy.code;
                    updateReport.mutate({
                      sublimitInfo: {
                        extracted_sublimits: {
                          ...extractedSublimitInfo,
                          [policyKey]: extractedSublimitInfo[policyKey].map(
                            (limit) =>
                              limit.sublimit_name === data.sublimit_name
                                ? { ...limit, is_removed: true }
                                : limit
                          ),
                        },
                        manual_sublimits: manualSublimits,
                      },
                    });
                  }
                };

                return (
                  <div className={styles.horizontalContainer}>
                    <Button
                      icon="pi pi-pencil"
                      style={{ width: "28px", height: "28px" }}
                      text
                      disabled={updateReport.isPending}
                      onClick={handleEdit}
                    />
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      style={{ width: "28px", height: "28px" }}
                      text
                      disabled={updateReport.isPending}
                      onClick={handleDelete}
                    />
                  </div>
                );
              }}
            />
          </DataTable>
        </TabPanel>
        <TabPanel header="Deductibles">
          <div style={{ marginBottom: "12px" }}>
            <Button
              size="small"
              disabled={updateReport.isPending}
              icon="pi pi-plus"
              label="Add deductible"
              onClick={() => setAddDeductibleVisible(true)}
            />
            <AddManualEntryDialog
              visible={addDeductibleVisible}
              header="Add Deductible"
              namePlaceholder="Deductible name"
              descriptionPlaceholder="Deductible description"
              onHide={() => setAddDeductibleVisible(false)}
              onConfirm={(peril, deductibleName, deductibleValue) => {
                const formatted = formatMonetaryValue(deductibleValue);
                setDeductibleInfo((prev) => {
                  const next = cloneDeep(
                    prev ?? {
                      extracted_deductibles: {},
                      manual_deductibles: {},
                    }
                  );
                  const existing = next.manual_deductibles[peril];
                  if (existing) {
                    existing.manual_excerpt_overrides = {
                      ...existing.manual_excerpt_overrides,
                      [deductibleName]: formatted,
                    };
                  } else {
                    next.manual_deductibles[peril] = {
                      peril_type: peril,
                      short_deductible_description: "",
                      executable_python_function_str: "",
                      direct_excerpts: {},
                      manual_excerpt_overrides: {
                        [deductibleName]: formatted,
                      },
                    };
                  }
                  return next;
                });
              }}
            />
          </div>
          <DataTable
            value={deductibleRows}
            scrollable
            scrollHeight={"70vh"}
            size="small"
            rowGroupMode="subheader"
            groupRowsBy="readablePerilName"
            rowGroupHeaderTemplate={(data: DeductibleRow) => (
              <strong>{data.readablePerilName}</strong>
            )}
            sortField="readablePerilName"
            sortOrder={1}
          >
            <Column header="Deductible" field="deductibleName" />
            <Column header="Value" field="value" />
            <Column
              header="From"
              body={(data: DeductibleRow) => (
                <>
                  {data.manual ? (
                    <i className="pi pi-user" />
                  ) : (
                    <i className="pi pi-file" />
                  )}
                </>
              )}
            />
            <Column
              header=""
              body={(data: DeductibleRow) => (
                <Button
                  icon="pi pi-pencil"
                  style={{ width: "28px", height: "28px" }}
                  text
                  disabled={updateReport.isPending}
                  onClick={() => handleDeductibleEdit(data)}
                />
              )}
            />
          </DataTable>
        </TabPanel>
      </TabView>
    </Card>
  );
};
